import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCallback } from "react";

import {
  nextScoutWriteMode,
  SCOUT_WRITE_MODE_GQL,
  SCOUT_WRITE_MODE_OPTIONS,
  type ScoutWriteModeGql,
} from "./scoutWriteMode";

export const SCOUT_WRITE_ACCESS_CONTROL_LABEL = "Scout data changes";

export const SCOUT_WRITE_ACCESS_INFO_TOOLTIP =
  "Choose whether Scout can change your Encamp data using your permissions: Off (read-only), Ask before applying, or Apply immediately.";

function getIndicatorTooltip(writeMode: ScoutWriteModeGql): string {
  if (writeMode === SCOUT_WRITE_MODE_GQL.AUTO) {
    return "Scout applies data changes immediately. Click to cycle write mode.";
  }
  if (writeMode === SCOUT_WRITE_MODE_GQL.ASK) {
    return "Scout asks before applying data changes. Click to cycle write mode.";
  }
  return "Scout is read-only. Click to cycle write mode.";
}

function getIndicatorAriaLabel(writeMode: ScoutWriteModeGql): string {
  if (writeMode === SCOUT_WRITE_MODE_GQL.AUTO) {
    return "Scout applies data changes immediately. Click to change write mode.";
  }
  if (writeMode === SCOUT_WRITE_MODE_GQL.ASK) {
    return "Scout asks before applying data changes. Click to change write mode.";
  }
  return "Scout is read-only. Click to change write mode.";
}

export type UseScoutWriteAccessControlInput = {
  writeMode: ScoutWriteModeGql;
  onWriteModeChange: (next: ScoutWriteModeGql) => void;
  disabled?: boolean;
  hasScoutProAccess?: boolean;
};

export function useScoutWriteAccessControl(input: UseScoutWriteAccessControlInput) {
  const { featureFlags } = useFeatureFlags();
  const hasScoutProAccess = input.hasScoutProAccess ?? true;
  const isFeatureEnabled =
    hasScoutProAccess && Boolean(featureFlags?.[FeatureFlagKey.ScoutWriteAccess]);
  const disabled = input.disabled ?? false;

  const cycle = useCallback(() => {
    if (disabled || !hasScoutProAccess) {
      return;
    }

    input.onWriteModeChange(nextScoutWriteMode(input.writeMode));
  }, [disabled, hasScoutProAccess, input]);

  return {
    isFeatureEnabled,
    writeMode: input.writeMode,
    disabled,
    cycle,
    /** @deprecated Prefer `cycle` — kept for call sites that still say toggle. */
    toggle: cycle,
    controlLabel: SCOUT_WRITE_ACCESS_CONTROL_LABEL,
    infoTooltip: SCOUT_WRITE_ACCESS_INFO_TOOLTIP,
    options: SCOUT_WRITE_MODE_OPTIONS,
    indicatorTooltip: getIndicatorTooltip(input.writeMode),
    indicatorAriaLabel: getIndicatorAriaLabel(input.writeMode),
  };
}
